import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Tag, Pencil, ArrowLeft } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Coupon {
  id: string;
  code: string;
  label: string;
  partner_org: string | null;
  discount_type: "percent" | "flat" | "full";
  discount_value: number;
  max_redemptions: number | null;
  redemption_count: number;
  valid_from: string | null;
  valid_until: string | null;
  active: boolean;
  notes: string | null;
  created_at: string;
}

const emptyForm = {
  code: "",
  label: "",
  partner_org: "",
  discount_type: "percent" as "percent" | "flat" | "full",
  discount_value: "50",
  max_redemptions: "",
  valid_until: "",
  notes: "",
  active: true,
};

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("coupons")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error && data) setCoupons(data as Coupon[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (c: Coupon) => {
    setEditing(c);
    setForm({
      code: c.code,
      label: c.label,
      partner_org: c.partner_org || "",
      discount_type: c.discount_type,
      discount_value: String(c.discount_value),
      max_redemptions: c.max_redemptions?.toString() || "",
      valid_until: c.valid_until ? c.valid_until.slice(0, 10) : "",
      notes: c.notes || "",
      active: c.active,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.code.trim() || !form.label.trim()) {
      toast.error("Code and label are required");
      return;
    }
    setSaving(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      label: form.label.trim(),
      partner_org: form.partner_org.trim() || null,
      discount_type: form.discount_type,
      discount_value:
        form.discount_type === "full" ? 0 : Number(form.discount_value) || 0,
      max_redemptions: form.max_redemptions
        ? Number(form.max_redemptions)
        : null,
      valid_until: form.valid_until
        ? new Date(form.valid_until).toISOString()
        : null,
      notes: form.notes.trim() || null,
      active: form.active,
    };

    const { error } = editing
      ? await supabase.from("coupons").update(payload).eq("id", editing.id)
      : await supabase.from("coupons").insert({
          ...payload,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        });

    setSaving(false);

    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(editing ? "Coupon updated" : "Coupon created");
    setDialogOpen(false);
    load();
  };

  const toggleActive = async (c: Coupon) => {
    const { error } = await supabase
      .from("coupons")
      .update({ active: !c.active })
      .eq("id", c.id);
    if (error) toast.error(error.message);
    else load();
  };

  const describe = (c: Coupon) => {
    if (c.discount_type === "full") return "100% scholarship";
    if (c.discount_type === "percent") return `${c.discount_value}% off`;
    return `₹${c.discount_value} off`;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-6xl mx-auto">
          <WeKitLogo size="sm" />
          <Badge variant="outline">Admin</Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin/payments">
                <ArrowLeft className="h-4 w-4" /> Payments
              </Link>
            </Button>
            <h1 className="font-display text-2xl font-bold text-foreground">
              NGO Coupons & Scholarship Codes
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/coupons/history">History & audit log</Link>
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate}>
                  <Plus className="h-4 w-4" /> New coupon
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit coupon" : "Create coupon"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Code</Label>
                    <Input
                      placeholder="NGO-ASHA-2026"
                      value={form.code}
                      onChange={(e) =>
                        setForm({ ...form, code: e.target.value.toUpperCase() })
                      }
                      maxLength={50}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Partner / NGO</Label>
                    <Input
                      placeholder="Asha Foundation"
                      value={form.partner_org}
                      onChange={(e) =>
                        setForm({ ...form, partner_org: e.target.value })
                      }
                      maxLength={120}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Label (shown internally)</Label>
                  <Input
                    placeholder="Asha Foundation Scholarship — Batch 1"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    maxLength={200}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Discount type</Label>
                    <Select
                      value={form.discount_type}
                      onValueChange={(v: "percent" | "flat" | "full") =>
                        setForm({ ...form, discount_type: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">% off</SelectItem>
                        <SelectItem value="flat">Flat ₹ off</SelectItem>
                        <SelectItem value="full">100% scholarship</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>
                      {form.discount_type === "percent"
                        ? "Percent (0–100)"
                        : form.discount_type === "flat"
                          ? "Amount (₹)"
                          : "—"}
                    </Label>
                    <Input
                      type="number"
                      value={form.discount_value}
                      onChange={(e) =>
                        setForm({ ...form, discount_value: e.target.value })
                      }
                      disabled={form.discount_type === "full"}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Max redemptions (blank = unlimited)</Label>
                    <Input
                      type="number"
                      placeholder="e.g. 100"
                      value={form.max_redemptions}
                      onChange={(e) =>
                        setForm({ ...form, max_redemptions: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Valid until</Label>
                    <Input
                      type="date"
                      value={form.valid_until}
                      onChange={(e) =>
                        setForm({ ...form, valid_until: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Notes</Label>
                  <Input
                    placeholder="Internal notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    maxLength={500}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                  />
                  <span className="text-sm">Active</span>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setDialogOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button onClick={save} disabled={saving}>
                  {saving ? "Saving..." : editing ? "Save" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Tag className="h-5 w-5 text-primary" /> All coupons
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : coupons.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No coupons yet. Create one to give NGO students a discount or
                full scholarship.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Partner</TableHead>
                      <TableHead>Discount</TableHead>
                      <TableHead>Redeemed</TableHead>
                      <TableHead>Valid until</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {coupons.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono font-semibold text-foreground">
                          {c.code}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {c.partner_org || "—"}
                        </TableCell>
                        <TableCell className="text-foreground">
                          {describe(c)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {c.redemption_count}
                          {c.max_redemptions ? ` / ${c.max_redemptions}` : ""}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {c.valid_until
                            ? new Date(c.valid_until).toLocaleDateString(
                                "en-IN",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                },
                              )
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={c.active}
                            onCheckedChange={() => toggleActive(c)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(c)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminCoupons;
